/**
 * What the transfer form says — DECISIONS.md #48.
 *
 * **Every code gets a message.** {@link transferRefusalMessage} is exhaustive over
 * {@link TransferRefusalCode} by construction, so a code added on the server and typed into
 * `TRANSFER_REFUSAL_CODES` fails the typecheck here until somebody has written the wording.
 *
 * **Every time goes through the shared formatter** (AGENTS.md §5), including the one inside a
 * refusal: `TRANSFER_BEFORE_CURRENT` carries since-when as an instant, and a refusal that printed it
 * raw would be the one place on the screen a clinical time looked different.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import type { TransferRefusalCode, TransferRefusalParams } from './transferApi'

export type TransferTranslate = TFunction<['common', 'patient', 'transfer']>

/** An instant as the record's one format, or the formatter's own fallback for an unreadable one. */
function since(instant: string | undefined, t: TransferTranslate): string {
  try {
    return formatClinicalDateTime(instant ?? '')
  } catch {
    return t('common:value.unreadable')
  }
}

/** The sentence for a refusal, in the interface language. */
export function transferRefusalMessage(
  code: TransferRefusalCode | 'UNKNOWN',
  params: TransferRefusalParams,
  t: TransferTranslate,
): string {
  switch (code) {
    case 'TRANSFER_WARD_MISSING':
      return t('transfer:refusal.wardMissing')

    case 'TRANSFER_WARD_NOT_FOUND':
      return t('transfer:refusal.wardNotFound', { ward: params.ward ?? '' })

    case 'TRANSFER_FIELD_TOO_LONG':
      return t('transfer:refusal.fieldTooLong', { max: params.max ?? 0 })

    case 'TRANSFER_MOVED_AT_MISSING':
      return t('transfer:refusal.movedAtMissing')

    case 'TRANSFER_MOVED_AT_MALFORMED':
      return t('transfer:refusal.movedAtMalformed')

    case 'TRANSFER_MOVED_AT_IN_FUTURE':
      return t('transfer:refusal.movedAtInFuture')

    case 'TRANSFER_BEFORE_CURRENT':
      return t('transfer:refusal.beforeCurrent', { since: since(params.since, t) })

    case 'TRANSFER_NO_CHANGE':
      return t('transfer:refusal.noChange')

    case 'TRANSFER_VERSION_STALE':
      return t('transfer:refusal.stale')

    case 'TRANSFER_ENCOUNTER_NOT_ACTIVE':
      return t('transfer:refusal.notActive')

    case 'TRANSFER_ENCOUNTER_NOT_FOUND':
      return t('transfer:refusal.notFound')

    case 'ROLE_MAY_NOT_DOCUMENT':
      return t('transfer:refusal.roleMayNotDocument')

    // A body or a version the form always sends. Reaching either is a client defect, not something
    // the clinician can fix, so it is the generic sentence rather than one that blames a field.
    case 'TRANSFER_BODY_MISSING':
    case 'TRANSFER_SUPERSEDES_MISSING':
    case 'UNKNOWN':
      return t('transfer:refusal.unknown')
  }
}

/**
 * The sentence for a request that got no usable answer.
 *
 * Not "the patient was not moved": the request may have reached the server and been written before
 * the answer was lost, so the honest instruction is to look before moving them again.
 */
export function transferUnavailableMessage(t: TransferTranslate): string {
  return t('transfer:refusal.unavailable')
}

/** "since …" for the current placement, through the shared formatter. */
export function transferSince(instant: string, t: TransferTranslate): string {
  return since(instant, t)
}
